import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { adminAPI } from '../../utils/api';
import { formatDateDDMMYYYY, formatDateWithWeekday, getTodayYYYYMMDD } from '../../utils/dateFormat';
import { toast } from 'sonner';
import { ArrowLeft, Calendar, Download, RefreshCw, Users, Clock, CheckCircle2, AlertTriangle, Activity, MapPin, Search, Filter, XCircle } from 'lucide-react';

const SupervisorMonitoring = () => {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [data, setData] = useState(null);
  const [gpsData, setGpsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedSupervisor, setSelectedSupervisor] = useState(null);
  const [showGpsModal, setShowGpsModal] = useState(false);
  const [showParkListModal, setShowParkListModal] = useState(null); // 'assigned', 'completed', 'pending'
  
  // GPS tracker filters
  const [gpsSearchTerm, setGpsSearchTerm] = useState('');
  const [gpsFilterSupervisor, setGpsFilterSupervisor] = useState('');
  const [gpsFilterStatus, setGpsFilterStatus] = useState('');
  
  const { user, userType, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const hasFetched = useRef(false);

  // Auth check only
  useEffect(() => {
    if (authLoading) return;
    if (!user || userType !== 'admin') {
      navigate('/admin/login');
    }
  }, [authLoading, user, userType, navigate]);

  // Initial fetch - runs once
  useEffect(() => {
    if (authLoading || !user || userType !== 'admin') return;
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchData();
  }, [authLoading, user, userType]);

  // Fetch on date change (user action)
  const handleDateChange = (newDate) => {
    setDate(newDate);
    fetchDataForDate(newDate);
  };

  const fetchDataForDate = async (targetDate) => {
    setLoading(true);
    try {
      const response = await adminAPI.getSupervisorMonitoring(targetDate);
      setData(response.data);
      
      // Also fetch GPS status
      try {
        const gpsResponse = await adminAPI.getGpsStatus(targetDate);
        setGpsData(gpsResponse.data);
      } catch (gpsError) {
        console.log('GPS data not available');
      }
    } catch (error) {
      toast.error('Failed to load monitoring data');
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async (isManualRefresh = false) => {
    if (isManualRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const response = await adminAPI.getSupervisorMonitoring(date);
      setData(response.data);
      
      // Also fetch GPS status
      try {
        const gpsResponse = await adminAPI.getGpsStatus(date);
        setGpsData(gpsResponse.data);
      } catch (gpsError) {
        console.log('GPS data not available');
      }
      
      if (isManualRefresh) {
        toast.success('Data refreshed');
      }
    } catch (error) {
      toast.error('Failed to load monitoring data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchData(true);
  };

  const downloadExcel = () => {
    if (!data?.supervisors || data.supervisors.length === 0) {
      toast.error('No data to download');
      return;
    }

    let rows = [];
    rows.push(['Supervisor ID', 'Supervisor Name', 'Entry Time', 'Last Active', 'Assigned Parks', 'Completed Parks', 'Pending Parks', 'Avg Time (min)', 'First Inspection', 'Last Inspection', 'Achievement %']);
    
    data.supervisors.forEach(s => {
      rows.push([
        s.supervisor_id,
        s.supervisor_name,
        s.entry_time,
        s.last_active_time,
        s.assigned_parks,
        s.completed_parks,
        s.pending_parks,
        s.avg_time_minutes,
        s.first_inspection,
        s.last_inspection,
        s.achievement_percent
      ]);
    });

    const csvContent = rows.map(row => 
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Supervisor_Monitoring_${date}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success('Downloaded successfully');
  };

  // Download GPS data as Excel
  const downloadGpsExcel = () => {
    if (!gpsData?.data || gpsData.data.length === 0) {
      toast.error('No GPS data to download');
      return;
    }

    const filteredData = getFilteredGpsData();
    
    let rows = [];
    rows.push([
      'Supervisor ID', 'Supervisor Name', 'Park ID', 'Park Name', 'Zone', 'Visit Time',
      'Start GPS Lat', 'Start GPS Lng', 'Start Distance (m)', 'Start Status',
      'End GPS Lat', 'End GPS Lng', 'End Distance (m)', 'End Status',
      'Park GPS Lat', 'Park GPS Lng'
    ]);
    
    filteredData.forEach(item => {
      rows.push([
        item.supervisor_id,
        item.supervisor_name,
        item.park_id,
        item.park_name,
        item.zone,
        item.visit_time,
        item.start_gps?.lat || '-',
        item.start_gps?.lng || '-',
        item.start_distance_meters !== null && item.start_distance_meters !== undefined ? item.start_distance_meters : '-',
        item.start_within_range === true ? 'Within Range' : item.start_within_range === false ? 'Out of Range' : 'GPS Unavailable',
        item.end_gps?.lat || '-',
        item.end_gps?.lng || '-',
        item.end_distance_meters !== null && item.end_distance_meters !== undefined ? item.end_distance_meters : '-',
        item.end_within_range === true ? 'Within Range' : item.end_within_range === false ? 'Out of Range' : 'GPS Unavailable',
        item.park_gps?.lat || '-',
        item.park_gps?.lng || '-'
      ]);
    });

    const csvContent = rows.map(row => 
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `GPS_Tracker_${date}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success('GPS data downloaded');
  };

  // Filter GPS data
  const getFilteredGpsData = () => {
    if (!gpsData?.data) return [];
    
    return gpsData.data.filter(item => {
      // Search filter
      if (gpsSearchTerm) {
        const search = gpsSearchTerm.toLowerCase();
        const matchesSearch = 
          item.supervisor_name?.toLowerCase().includes(search) ||
          item.supervisor_id?.toLowerCase().includes(search) ||
          item.park_name?.toLowerCase().includes(search) ||
          item.park_id?.toLowerCase().includes(search) ||
          item.zone?.toLowerCase().includes(search);
        if (!matchesSearch) return false;
      }
      
      // Supervisor filter
      if (gpsFilterSupervisor && item.supervisor_id !== gpsFilterSupervisor) {
        return false;
      }
      
      // Status filter - now supports start/end/both filters
      if (gpsFilterStatus === 'start_within' && item.start_within_range !== true) return false;
      if (gpsFilterStatus === 'start_out' && item.start_within_range !== false) return false;
      if (gpsFilterStatus === 'start_unavailable' && item.start_within_range !== null) return false;
      if (gpsFilterStatus === 'end_within' && item.end_within_range !== true) return false;
      if (gpsFilterStatus === 'end_out' && item.end_within_range !== false) return false;
      if (gpsFilterStatus === 'end_unavailable' && item.end_within_range !== null) return false;
      if (gpsFilterStatus === 'both_out' && !(item.start_within_range === false && item.end_within_range === false)) return false;
      // Legacy filters
      if (gpsFilterStatus === 'within' && item.within_range !== true) return false;
      if (gpsFilterStatus === 'out' && item.within_range !== false) return false;
      if (gpsFilterStatus === 'unavailable' && item.within_range !== null) return false;
      
      return true;
    });
  };

  // Get unique supervisors for dropdown
  const getUniqueSupervisors = () => {
    if (!gpsData?.data) return [];
    const supervisors = new Map();
    gpsData.data.forEach(item => {
      if (item.supervisor_id && !supervisors.has(item.supervisor_id)) {
        supervisors.set(item.supervisor_id, item.supervisor_name);
      }
    });
    return Array.from(supervisors.entries()).map(([id, name]) => ({ id, name }));
  };

  const getAchievementColor = (percent) => {
    if (percent >= 80) return 'bg-green-100 text-green-700 border-green-300';
    if (percent >= 50) return 'bg-yellow-100 text-yellow-700 border-yellow-300';
    return 'bg-red-100 text-red-700 border-red-300';
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-slate-900 text-white p-4 shadow-md">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <Link to="/admin/dashboard" className="hover:opacity-80">
            <ArrowLeft className="w-6 h-6" />
          </Link>
          <div className="flex-1">
            <h1 className="text-2xl font-bold uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Supervisor Monitoring
            </h1>
            <p className="text-sm opacity-90">Track supervisor performance and productivity</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 md:p-6">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <Calendar className="w-5 h-5 text-slate-600" />
            <input
              type="date"
              value={date}
              onChange={(e) => handleDateChange(e.target.value)}
              className="h-10 px-4 rounded-lg border-2 border-slate-300 focus:border-blue-600 focus:ring-0"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="h-10 px-4 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg font-semibold flex items-center gap-2"
              data-testid="refresh-monitoring-btn"
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
            <button
              onClick={downloadExcel}
              className="h-10 px-4 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold flex items-center gap-2"
            >
              <Download className="w-5 h-5" />
              Download Excel
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        {data?.summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-l-purple-500">
              <div className="flex items-center gap-3">
                <Users className="w-8 h-8 text-purple-600" />
                <div>
                  <div className="text-2xl font-bold text-purple-600">{data.summary.total_supervisors}</div>
                  <div className="text-xs font-semibold text-slate-600">Total Supervisors</div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-l-blue-500">
              <div className="flex items-center gap-3">
                <Activity className="w-8 h-8 text-blue-600" />
                <div>
                  <div className="text-2xl font-bold text-blue-600">{data.summary.logged_in}</div>
                  <div className="text-xs font-semibold text-slate-600">Logged In Today</div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-l-yellow-500">
              <div className="flex items-center gap-3">
                <Clock className="w-8 h-8 text-yellow-600" />
                <div>
                  <div className="text-2xl font-bold text-yellow-600">
                    {data.summary.total_parks}
                    <span className="text-lg text-slate-400">/{data.summary.total_parks}</span>
                  </div>
                  <div className="text-xs font-semibold text-slate-600">
                    Assigned Parks
                    {data.summary.total_renovation > 0 && (
                      <span className="text-amber-500 ml-1">
                        ({data.summary.total_renovation} renovation)
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-l-green-500">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
                <div>
                  <div className="text-2xl font-bold text-green-600">
                    {data.summary.total_completed}
                    <span className="text-lg text-slate-400">/{data.summary.expected_inspections || data.summary.total_parks}</span>
                  </div>
                  <div className="text-xs font-semibold text-slate-600">Completed Inspections</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* GPS Status Section */}
        {gpsData?.summary && (
          <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MapPin className="w-6 h-6 text-blue-600" />
                <div>
                  <h3 className="font-bold">GPS Verification Status</h3>
                  <p className="text-sm text-slate-500">Range: {gpsData.gps_range_meters}m</p>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                {/* Starting GPS */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold text-slate-500 w-16">Starting:</span>
                  <button 
                    onClick={() => { setGpsFilterStatus('start_within'); setShowGpsModal(true); }}
                    className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs font-bold hover:bg-green-200 cursor-pointer transition-colors"
                    data-testid="gps-start-within-btn"
                  >
                    {gpsData.summary.start_within_range || 0} In Range
                  </button>
                  <button 
                    onClick={() => { setGpsFilterStatus('start_out'); setShowGpsModal(true); }}
                    className="bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs font-bold hover:bg-red-200 cursor-pointer transition-colors"
                    data-testid="gps-start-out-btn"
                  >
                    {gpsData.summary.start_out_of_range || 0} Out
                  </button>
                  {(gpsData.summary.start_gps_unavailable > 0) && (
                    <button 
                      onClick={() => { setGpsFilterStatus('start_unavailable'); setShowGpsModal(true); }}
                      className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded text-xs font-bold hover:bg-gray-200 cursor-pointer transition-colors"
                      data-testid="gps-start-unavailable-btn"
                    >
                      {gpsData.summary.start_gps_unavailable} N/A
                    </button>
                  )}
                </div>
                {/* Ending GPS */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold text-slate-500 w-16">Ending:</span>
                  <button 
                    onClick={() => { setGpsFilterStatus('end_within'); setShowGpsModal(true); }}
                    className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs font-bold hover:bg-green-200 cursor-pointer transition-colors"
                    data-testid="gps-end-within-btn"
                  >
                    {gpsData.summary.end_within_range || 0} In Range
                  </button>
                  <button 
                    onClick={() => { setGpsFilterStatus('end_out'); setShowGpsModal(true); }}
                    className="bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs font-bold hover:bg-red-200 cursor-pointer transition-colors"
                    data-testid="gps-end-out-btn"
                  >
                    {gpsData.summary.end_out_of_range || 0} Out
                  </button>
                  {(gpsData.summary.end_gps_unavailable > 0) && (
                    <button 
                      onClick={() => { setGpsFilterStatus('end_unavailable'); setShowGpsModal(true); }}
                      className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded text-xs font-bold hover:bg-gray-200 cursor-pointer transition-colors"
                      data-testid="gps-end-unavailable-btn"
                    >
                      {gpsData.summary.end_gps_unavailable} N/A
                    </button>
                  )}
                </div>
                {/* Both Out of Range */}
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-slate-500 w-16">Both Out:</span>
                  <button 
                    onClick={() => { setGpsFilterStatus('both_out'); setShowGpsModal(true); }}
                    className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded text-xs font-bold hover:bg-orange-200 cursor-pointer transition-colors"
                    data-testid="gps-both-out-btn"
                  >
                    {gpsData.summary.both_out_of_range || 0} Inspections
                  </button>
                </div>
              </div>
              <button
                onClick={() => { setGpsFilterStatus(''); setShowGpsModal(true); }}
                className="h-10 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold text-sm whitespace-nowrap"
                data-testid="view-gps-details-btn"
              >
                View Details
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="p-4 border-b bg-slate-50">
              <h2 className="text-lg font-bold">Supervisor Performance</h2>
            </div>
            
            {!data?.supervisors || data.supervisors.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                No supervisor data available for this date
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-100 text-sm">
                      <th className="text-left p-3 font-bold">Supervisor</th>
                      <th className="text-center p-3 font-bold">Entry Time</th>
                      <th className="text-center p-3 font-bold">Last Active</th>
                      <th className="text-center p-3 font-bold">Assigned</th>
                      <th className="text-center p-3 font-bold">Renovation</th>
                      <th className="text-center p-3 font-bold">Completed</th>
                      <th className="text-center p-3 font-bold">Incomplete</th>
                      <th className="text-center p-3 font-bold">Pending</th>
                      <th className="text-center p-3 font-bold">Avg Time</th>
                      <th className="text-center p-3 font-bold">Achievement</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.supervisors.map((supervisor) => (
                      <tr 
                        key={supervisor.supervisor_id} 
                        className="border-t hover:bg-slate-50 cursor-pointer"
                        onClick={() => setSelectedSupervisor(selectedSupervisor?.supervisor_id === supervisor.supervisor_id ? null : supervisor)}
                        data-testid={`supervisor-row-${supervisor.supervisor_id}`}
                      >
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{supervisor.supervisor_name}</span>
                            {supervisor.is_doing_ot && (
                              <span className="bg-purple-600 text-white px-2 py-0.5 rounded text-xs font-bold" data-testid={`ot-badge-${supervisor.supervisor_id}`}>
                                OT
                              </span>
                            )}
                            {supervisor.is_absent && (
                              <span className="bg-red-500 text-white px-2 py-0.5 rounded text-xs font-bold" data-testid={`absent-badge-${supervisor.supervisor_id}`}>
                                ABSENT
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500">{supervisor.supervisor_id}</div>
                        </td>
                        <td className="p-3 text-center">
                          {supervisor.entry_time !== '-' ? (
                            <span className="text-green-600 font-semibold">{supervisor.entry_time}</span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          {supervisor.last_active_time !== '-' ? (
                            <span className="text-blue-600 font-semibold">{supervisor.last_active_time}</span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td className="p-3 text-center font-semibold">{supervisor.assigned_parks}</td>
                        <td className="p-3 text-center">
                          {(supervisor.renovation_parks || 0) > 0 ? (
                            <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full text-sm font-bold">
                              {supervisor.renovation_parks}
                            </span>
                          ) : (
                            <span className="text-slate-400">0</span>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-sm font-bold">
                            {supervisor.completed_parks}
                          </span>
                        </td>
                        <td className="p-3 text-center">
                          {(supervisor.incomplete_parks || 0) > 0 ? (
                            <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded-full text-sm font-bold">
                              {supervisor.incomplete_parks}
                            </span>
                          ) : (
                            <span className="text-slate-400">0</span>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          {supervisor.pending_parks > 0 ? (
                            <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded-full text-sm font-bold">
                              {supervisor.pending_parks}
                            </span>
                          ) : (
                            <span className="text-green-600">0</span>
                          )}
                        </td>
                        <td className="p-3 text-center text-sm">{supervisor.avg_time_minutes} min</td>
                        <td className="p-3 text-center">
                          <span className={`px-3 py-1 rounded-full text-sm font-bold border ${getAchievementColor(supervisor.achievement_percent)}`}>
                            {supervisor.achievement_percent}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Drill-down Modal */}
        {selectedSupervisor && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[80vh] overflow-hidden">
              <div className="bg-purple-600 text-white p-4 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-xl font-bold">{selectedSupervisor.supervisor_name}</h3>
                    {selectedSupervisor.is_doing_ot && (
                      <span className="bg-yellow-400 text-purple-900 px-2 py-0.5 rounded text-xs font-bold">
                        OT DUTY
                      </span>
                    )}
                    {selectedSupervisor.is_absent && (
                      <span className="bg-red-500 text-white px-2 py-0.5 rounded text-xs font-bold">
                        ABSENT
                      </span>
                    )}
                  </div>
                  <p className="text-sm opacity-90">{selectedSupervisor.supervisor_id} • {formatDateDDMMYYYY(date)}</p>
                </div>
                <button 
                  onClick={() => setSelectedSupervisor(null)}
                  className="p-2 hover:bg-white/20 rounded-full"
                >
                  ✕
                </button>
              </div>
              
              <div className="p-4 overflow-y-auto max-h-[60vh]">
                <div className="grid grid-cols-4 gap-4 mb-4">
                  <div 
                    className="bg-slate-100 rounded-lg p-3 text-center cursor-pointer hover:bg-slate-200 transition-colors"
                    onClick={() => setShowParkListModal('assigned')}
                    data-testid="assigned-count-box"
                  >
                    <div className="text-2xl font-bold text-slate-700">{selectedSupervisor.assigned_parks}</div>
                    <div className="text-xs text-slate-500">Assigned (Click to view)</div>
                  </div>
                  <div 
                    className="bg-green-100 rounded-lg p-3 text-center cursor-pointer hover:bg-green-200 transition-colors"
                    onClick={() => setShowParkListModal('completed')}
                    data-testid="completed-count-box"
                  >
                    <div className="text-2xl font-bold text-green-700">{selectedSupervisor.completed_parks}</div>
                    <div className="text-xs text-green-600">Completed (Click to view)</div>
                  </div>
                  <div 
                    className="bg-orange-100 rounded-lg p-3 text-center cursor-pointer hover:bg-orange-200 transition-colors"
                    onClick={() => setShowParkListModal('pending')}
                    data-testid="pending-count-box"
                  >
                    <div className="text-2xl font-bold text-orange-700">{selectedSupervisor.pending_parks}</div>
                    <div className="text-xs text-orange-600">Pending (Click to view)</div>
                  </div>
                  <div className="bg-blue-100 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-blue-700">{selectedSupervisor.avg_time_minutes}</div>
                    <div className="text-xs text-blue-600">Avg Min/Park</div>
                  </div>
                </div>

                {/* Park List View */}
                {showParkListModal && (
                  <div className="mb-4 bg-slate-50 rounded-lg border-2 border-slate-200 overflow-hidden">
                    <div className="bg-slate-200 p-3 flex items-center justify-between">
                      <h4 className="font-bold">
                        {showParkListModal === 'assigned' && 'All Assigned Parks'}
                        {showParkListModal === 'completed' && 'Completed Parks'}
                        {showParkListModal === 'pending' && 'Pending Parks'}
                      </h4>
                      <button 
                        onClick={() => setShowParkListModal(null)}
                        className="text-slate-600 hover:text-slate-800"
                      >
                        <XCircle className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-100 sticky top-0">
                          <tr>
                            <th className="text-left p-2 font-bold">Park ID</th>
                            <th className="text-left p-2 font-bold">Park Name</th>
                            <th className="text-center p-2 font-bold">Zone</th>
                            <th className="text-center p-2 font-bold">Division</th>
                            <th className="text-center p-2 font-bold">Manpower</th>
                            <th className="text-center p-2 font-bold">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const assignedParkIds = selectedSupervisor.assigned_park_ids || [];
                            const completedParkIds = (selectedSupervisor.inspections || []).map(i => i.park_id);
                            
                            let parksToShow = [];
                            
                            if (showParkListModal === 'assigned') {
                              parksToShow = (selectedSupervisor.all_parks || []);
                            } else if (showParkListModal === 'completed') {
                              // Use division and manpower directly from inspections (now populated by backend)
                              parksToShow = (selectedSupervisor.inspections || []).map(insp => {
                                return {
                                  park_id: insp.park_id,
                                  park_name: insp.park_name,
                                  zone: insp.zone,
                                  division: insp.division || '-',
                                  manpower_count: insp.manpower_count ?? 0,
                                  status: insp.park_status,
                                  inspection_time: insp.start_time,
                                  is_ot_park: insp.is_ot_park
                                };
                              });
                            } else if (showParkListModal === 'pending') {
                              parksToShow = (selectedSupervisor.all_parks || []).filter(p => 
                                !completedParkIds.includes(p.park_id) && p.is_active !== false
                              );
                            }
                            
                            if (parksToShow.length === 0) {
                              return (
                                <tr>
                                  <td colSpan="6" className="p-4 text-center text-slate-500">No parks to display</td>
                                </tr>
                              );
                            }
                            
                            return parksToShow.map((park, idx) => (
                              <tr key={idx} className={`border-t hover:bg-slate-50 ${park.is_ot_park ? 'bg-purple-50' : ''}`}>
                                <td className="p-2 font-medium">
                                  <div className="flex items-center gap-1">
                                    {park.park_id}
                                    {park.is_ot_park && (
                                      <span className="bg-purple-600 text-white px-1.5 py-0.5 rounded text-[10px] font-bold">OT</span>
                                    )}
                                  </div>
                                </td>
                                <td className="p-2">{park.park_name}</td>
                                <td className="p-2 text-center">{park.zone}</td>
                                <td className="p-2 text-center">{park.division || '-'}</td>
                                <td className="p-2 text-center font-semibold">{park.manpower_count ?? park.manpower_list?.length ?? 0}</td>
                                <td className="p-2 text-center">
                                  {showParkListModal === 'completed' ? (
                                    <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs font-bold">
                                      Done
                                    </span>
                                  ) : park.is_active === false ? (
                                    <span className="bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded text-xs font-bold">
                                      Renovation
                                    </span>
                                  ) : completedParkIds.includes(park.park_id) ? (
                                    <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs font-bold">
                                      Done
                                    </span>
                                  ) : (
                                    <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded text-xs font-bold">
                                      Pending
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ));
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                
                <h4 className="font-bold mb-3">Inspections ({selectedSupervisor.inspections?.length || 0})</h4>
                
                {selectedSupervisor.inspections?.length > 0 ? (
                  <div className="space-y-2">
                    {selectedSupervisor.inspections.map((insp, idx) => (
                      <div key={idx} className="bg-slate-50 rounded-lg p-3 border">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-semibold">{insp.park_name}</div>
                            <div className="text-sm text-slate-500">{insp.zone} • {insp.division || '-'}</div>
                          </div>
                          <div className="text-right">
                            <span className={`px-2 py-1 rounded text-xs font-bold ${
                              insp.park_status === 'Open' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {insp.park_status}
                            </span>
                          </div>
                        </div>
                        {/* Park Visit Duration Details */}
                        <div className="mt-2 flex items-center gap-4 text-xs">
                          <div className="flex items-center gap-1">
                            <span className="text-slate-500">Start:</span>
                            <span className="font-semibold text-green-600">{insp.start_time_ist || '-'}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-slate-500">End:</span>
                            <span className="font-semibold text-blue-600">{insp.end_time_ist || '-'}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-slate-500">Duration:</span>
                            <span className="font-bold text-purple-600">{insp.duration_minutes || 0} min</span>
                          </div>
                        </div>
                        {insp.cleanliness && (
                          <div className="text-xs mt-2">
                            Cleanliness: <span className={`font-bold ${
                              insp.cleanliness === 'Good' ? 'text-green-600' : 
                              insp.cleanliness === 'Average' ? 'text-yellow-600' : 'text-red-600'
                            }`}>{insp.cleanliness || '-'}</span>
                            {/* GPS Status */}
                            {gpsData?.data && (() => {
                              const gpsInfo = gpsData.data.find(g => g.inspection_id === insp.inspection_id);
                              if (gpsInfo) {
                                return (
                                  <span className="ml-3">
                                    GPS: <span className={`font-bold ${gpsInfo.within_range === true ? 'text-green-600' : gpsInfo.within_range === false ? 'text-red-600' : 'text-slate-500'}`}>
                                      {gpsInfo.gps_status}
                                    </span>
                                  </span>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    No inspections completed today
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* GPS Details Modal */}
      {showGpsModal && gpsData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
            <div className="bg-blue-600 text-white p-4 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold flex items-center gap-2">
                  <MapPin className="w-6 h-6" />
                  GPS Tracker
                </h3>
                <p className="text-sm opacity-90">{formatDateDDMMYYYY(date)} • Range: {gpsData.gps_range_meters}m</p>
              </div>
              <div className="flex items-center gap-2">
                <button 
                  onClick={downloadGpsExcel}
                  className="h-10 px-4 bg-white/20 hover:bg-white/30 rounded-lg font-semibold text-sm flex items-center gap-2"
                  data-testid="download-gps-excel"
                >
                  <Download className="w-4 h-4" />
                  Download Excel
                </button>
                <button 
                  onClick={() => {
                    setShowGpsModal(false);
                    setGpsSearchTerm('');
                    setGpsFilterSupervisor('');
                    setGpsFilterStatus('');
                  }}
                  className="p-2 hover:bg-white/20 rounded-full"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[75vh]">
              {/* Summary - Start/End Breakdown */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                {/* Start In Range */}
                <div 
                  className={`rounded-lg p-3 text-center border cursor-pointer transition-colors ${gpsFilterStatus === 'start_within' ? 'bg-green-200 border-green-500 ring-2 ring-green-500' : 'bg-green-50 border-green-200 hover:bg-green-100'}`}
                  onClick={() => setGpsFilterStatus(gpsFilterStatus === 'start_within' ? '' : 'start_within')}
                  data-testid="gps-modal-start-within"
                >
                  <div className="text-2xl font-bold text-green-600">{gpsData.summary.start_within_range || 0}</div>
                  <div className="text-xs text-green-700 font-semibold">Start In Range</div>
                </div>
                {/* Start Out */}
                <div 
                  className={`rounded-lg p-3 text-center border cursor-pointer transition-colors ${gpsFilterStatus === 'start_out' ? 'bg-red-200 border-red-500 ring-2 ring-red-500' : 'bg-red-50 border-red-200 hover:bg-red-100'}`}
                  onClick={() => setGpsFilterStatus(gpsFilterStatus === 'start_out' ? '' : 'start_out')}
                  data-testid="gps-modal-start-out"
                >
                  <div className="text-2xl font-bold text-red-600">{gpsData.summary.start_out_of_range || 0}</div>
                  <div className="text-xs text-red-700 font-semibold">Start Out</div>
                </div>
                {/* End In Range */}
                <div 
                  className={`rounded-lg p-3 text-center border cursor-pointer transition-colors ${gpsFilterStatus === 'end_within' ? 'bg-green-200 border-green-500 ring-2 ring-green-500' : 'bg-green-50 border-green-200 hover:bg-green-100'}`}
                  onClick={() => setGpsFilterStatus(gpsFilterStatus === 'end_within' ? '' : 'end_within')}
                  data-testid="gps-modal-end-within"
                >
                  <div className="text-2xl font-bold text-green-600">{gpsData.summary.end_within_range || 0}</div>
                  <div className="text-xs text-green-700 font-semibold">End In Range</div>
                </div>
                {/* End Out */}
                <div 
                  className={`rounded-lg p-3 text-center border cursor-pointer transition-colors ${gpsFilterStatus === 'end_out' ? 'bg-red-200 border-red-500 ring-2 ring-red-500' : 'bg-red-50 border-red-200 hover:bg-red-100'}`}
                  onClick={() => setGpsFilterStatus(gpsFilterStatus === 'end_out' ? '' : 'end_out')}
                  data-testid="gps-modal-end-out"
                >
                  <div className="text-2xl font-bold text-red-600">{gpsData.summary.end_out_of_range || 0}</div>
                  <div className="text-xs text-red-700 font-semibold">End Out</div>
                </div>
                {/* Both Out */}
                <div 
                  className={`rounded-lg p-3 text-center border cursor-pointer transition-colors ${gpsFilterStatus === 'both_out' ? 'bg-orange-200 border-orange-500 ring-2 ring-orange-500' : 'bg-orange-50 border-orange-200 hover:bg-orange-100'}`}
                  onClick={() => setGpsFilterStatus(gpsFilterStatus === 'both_out' ? '' : 'both_out')}
                  data-testid="gps-modal-both-out"
                >
                  <div className="text-2xl font-bold text-orange-600">{gpsData.summary.both_out_of_range || 0}</div>
                  <div className="text-xs text-orange-700 font-semibold">Both Out</div>
                </div>
              </div>

              {/* Filters Row */}
              <div className="flex items-center gap-3 mb-4 flex-wrap bg-slate-50 p-3 rounded-lg">
                <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                  <Search className="w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    value={gpsSearchTerm}
                    onChange={(e) => setGpsSearchTerm(e.target.value)}
                    placeholder="Search supervisor, park, zone..."
                    className="flex-1 h-10 px-3 rounded-lg border-2 border-slate-300 focus:border-blue-600 focus:ring-0"
                    data-testid="gps-search-input"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Filter className="w-5 h-5 text-slate-400" />
                  <select
                    value={gpsFilterSupervisor}
                    onChange={(e) => setGpsFilterSupervisor(e.target.value)}
                    className="h-10 px-3 rounded-lg border-2 border-slate-300 focus:border-blue-600 focus:ring-0"
                    data-testid="gps-supervisor-filter"
                  >
                    <option value="">All Supervisors</option>
                    {getUniqueSupervisors().map(sup => (
                      <option key={sup.id} value={sup.id}>{sup.id} - {sup.name}</option>
                    ))}
                  </select>
                </div>
                {(gpsSearchTerm || gpsFilterSupervisor || gpsFilterStatus) && (
                  <button
                    onClick={() => {
                      setGpsSearchTerm('');
                      setGpsFilterSupervisor('');
                      setGpsFilterStatus('');
                    }}
                    className="h-10 px-4 bg-slate-200 hover:bg-slate-300 rounded-lg font-semibold text-sm"
                  >
                    Clear Filters
                  </button>
                )}
                <div className="text-sm text-slate-500">
                  Showing {getFilteredGpsData().length} of {gpsData.data.length}
                </div>
              </div>

              {/* Details Table */}
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="text-left p-3 font-bold">Supervisor</th>
                      <th className="text-left p-3 font-bold">Park</th>
                      <th className="text-center p-3 font-bold">Zone</th>
                      <th className="text-center p-3 font-bold">Visit Time</th>
                      <th className="text-center p-3 font-bold">Start Distance</th>
                      <th className="text-center p-3 font-bold">Start Status</th>
                      <th className="text-center p-3 font-bold">End Distance</th>
                      <th className="text-center p-3 font-bold">End Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getFilteredGpsData().length === 0 ? (
                      <tr>
                        <td colSpan="8" className="p-8 text-center text-slate-500">
                          No data matching your filters
                        </td>
                      </tr>
                    ) : (
                      getFilteredGpsData().map((item, idx) => (
                        <tr key={idx} className={`border-t ${item.start_within_range === false || item.end_within_range === false ? 'bg-red-50' : ''}`}>
                          <td className="p-3">
                            <div className="font-semibold">{item.supervisor_name}</div>
                            <div className="text-xs text-slate-500">{item.supervisor_id}</div>
                          </td>
                          <td className="p-3">
                            <div className="font-medium">{item.park_name}</div>
                            <div className="text-xs text-slate-500">{item.park_id}</div>
                          </td>
                          <td className="p-3 text-center">{item.zone}</td>
                          <td className="p-3 text-center text-xs">
                            {item.visit_time?.split(' ')[1] || '-'}
                          </td>
                          {/* Start Distance */}
                          <td className="p-3 text-center font-semibold">
                            {item.start_distance_meters !== null && item.start_distance_meters !== undefined ? (
                              item.start_distance_meters >= 1000 
                                ? `${(item.start_distance_meters / 1000).toFixed(1)} km`
                                : `${item.start_distance_meters} m`
                            ) : '-'}
                          </td>
                          {/* Start Status */}
                          <td className="p-3 text-center">
                            <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                              item.start_within_range === true 
                                ? 'bg-green-100 text-green-700' 
                                : item.start_within_range === false 
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-slate-100 text-slate-600'
                            }`}>
                              {item.start_within_range === true ? 'In Range' : item.start_within_range === false ? 'Out' : 'N/A'}
                            </span>
                          </td>
                          {/* End Distance */}
                          <td className="p-3 text-center font-semibold">
                            {item.end_distance_meters !== null && item.end_distance_meters !== undefined ? (
                              item.end_distance_meters >= 1000 
                                ? `${(item.end_distance_meters / 1000).toFixed(1)} km`
                                : `${item.end_distance_meters} m`
                            ) : '-'}
                          </td>
                          {/* End Status */}
                          <td className="p-3 text-center">
                            <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                              item.end_within_range === true 
                                ? 'bg-green-100 text-green-700' 
                                : item.end_within_range === false 
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-slate-100 text-slate-600'
                            }`}>
                              {item.end_within_range === true ? 'In Range' : item.end_within_range === false ? 'Out' : 'N/A'}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupervisorMonitoring;
